import { Trans } from "@lingui/macro";
import { forwardRef } from "react";

interface NativeGoogleButtonProps {
  onClick?: () => void;
  disabled?: boolean;
  theme?: "light" | "dark" | "neutral";
}

const NativeGoogleButton = forwardRef(
  (
    { onClick, disabled = false, theme = "light" }: NativeGoogleButtonProps,
    ref: React.Ref<HTMLButtonElement>
  ) => {
    // Theme configurations based on Google's official guidelines
    const themes = {
      light: {
        backgroundColor: "#FFFFFF",
        color: "#1F1F1F",
        border: "1px solid #dadce0", // eslint-disable-line lingui/no-unlocalized-strings
      },
      dark: {
        backgroundColor: "#131314",
        color: "#E3E3E3",
        border: "1px solid #8E918F", // eslint-disable-line lingui/no-unlocalized-strings
      },
      neutral: {
        backgroundColor: "#F2F2F2",
        color: "#1F1F1F",
        border: "none", // eslint-disable-line lingui/no-unlocalized-strings
      },
    };

    const currentTheme = themes[theme] || themes.light;

    return (
      <button
        className="btn btn-sm google-btn btn-block"
        type="button"
        ref={ref}
        onClick={onClick}
        disabled={disabled}
        style={{
          backgroundColor: currentTheme.backgroundColor,
          color: currentTheme.color,
          border: currentTheme.border,
          borderRadius: "4px",
          padding: "0",
          fontSize: "14px",
          fontWeight: "500",
          fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40px",
          transition: "background-color 0.2s, box-shadow 0.2s",
          boxShadow: theme === "light" ? "0 1px 1px rgba(0,0,0,.1)" : "none",
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            if (theme === "light") {
              (e.target as HTMLButtonElement).style.backgroundColor = "#F8F9FA";
              (e.target as HTMLButtonElement).style.boxShadow =
                "0 1px 3px rgba(0,0,0,.2)";
            } else if (theme === "dark") {
              (e.target as HTMLButtonElement).style.backgroundColor = "#1E1E1E";
            } else {
              (e.target as HTMLButtonElement).style.backgroundColor = "#EEEEEE";
            }
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            (e.target as HTMLButtonElement).style.backgroundColor =
              currentTheme.backgroundColor;
            (e.target as HTMLButtonElement).style.boxShadow =
              theme === "light" ? "0 1px 1px rgba(0,0,0,.1)" : "none";
          }
        }}
      >
        {/* Official Google G logo with standard colors */}
        <div
          style={{
            padding: "12px 10px 12px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z"
              fill="#4285F4"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z"
              fill="#34A853"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957273C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z"
              fill="#FBBC05"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4636 0.891818 11.4264 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z"
              fill="#EA4335"
            />
          </svg>
        </div>

        <span
          style={{
            paddingRight: "12px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <Trans>Continua con Google</Trans>
        </span>
      </button>
    );
  }
);

export default NativeGoogleButton;
